import { updateWebsiteFlags as updateWebsiteFlagsAction } from '@va/dashboard/actions/api';
import { getWebsite } from '@va/dashboard/selectors/core';
import { useTranslate } from '@va/localization';
import { generateString, getWellFormedUrl, sleep } from '@va/util/helpers';
import { useListenForMessage } from '@va/util/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAddNotification } from './useAddNotification';

export enum AsyncStatus {
  INITIAL = 'INITIAL',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

type TrackingCodeValidationMessage = {
  type: 'TWIPLA_TRACKING_CODE_VALIDATION_REQUEST' | 'TWIPLA_TRACKING_CODE_VALIDATION_RESPONSE';
  websiteKey: string;
  requestId: string;
};

export function useTrackingCodeVerifier(onSuccessCallback: () => void) {
  const { id: websiteId, url: websiteUrl } = useSelector(getWebsite);

  const randomToken = useMemo(() => generateString(16), []);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const dispatch = useDispatch();
  const translate = useTranslate();

  const [status, setStatus] = useState<AsyncStatus>(AsyncStatus.INITIAL);
  const { showSuccessNotification, showErrorNotification } = useAddNotification();

  const updateTrackingCodeFlag = useCallback(
    (isTrackingCodeValid: boolean) => {
      dispatch(
        updateWebsiteFlagsAction({
          trackingCodeInstalled: isTrackingCodeValid,
          ssrCodeInstalled: isTrackingCodeValid,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      iframeRef.current?.remove();
      iframeRef.current = null;
    };
  }, []);

  const verify = useCallback(async () => {
    // await is needed here to prevent issues that can appear on some browsers after window.open is called, check VISA-7769
    // when window.open is called, it is interrupting React's process and causing the state updates to become out of sync
    await setStatus(AsyncStatus.IN_PROGRESS);

    try {
      const formattedUrl = getWellFormedUrl(websiteUrl);
      if (!formattedUrl) {
        throw new Error('URL not set');
      }
      iframeRef.current = document.createElement('iframe');
      iframeRef.current.name = JSON.stringify({ ignoreVisit: true });
      iframeRef.current.src = `${formattedUrl}?visa_worker_validate=${randomToken}&visa_worker_id=${websiteId}&cacheBuster=${String(
        Math.random(),
      )}`;
      iframeRef.current.style.display = 'none';
      document.body.appendChild(iframeRef.current);

      const maxDuration = 15000;
      const delay = 150;
      let elapsed = 0;

      while (elapsed + delay <= maxDuration) {
        if (!iframeRef.current) {
          // success
          return;
        }

        const msg: TrackingCodeValidationMessage = {
          type: 'TWIPLA_TRACKING_CODE_VALIDATION_REQUEST',
          websiteKey: websiteId,
          requestId: randomToken,
        };
        console.log('TRY VERIFY');
        iframeRef.current.contentWindow?.postMessage(msg, '*');

        await sleep(delay);
        elapsed += delay;
      }

      if (iframeRef.current) {
        iframeRef.current?.remove();
        iframeRef.current = null;
        showErrorNotification(5000, translate('notifications.trackingCode.snippetNotFound'));
        updateTrackingCodeFlag(false);
        setStatus(AsyncStatus.ERROR);
      }
    } catch (error) {
      console.warn(error);
      setStatus(AsyncStatus.ERROR);
      showErrorNotification(2000, translate('notifications.trackingCode.couldNotOpenPopup'));
      return;
    }
  }, [websiteUrl, randomToken, websiteId, showErrorNotification, translate, updateTrackingCodeFlag]);

  const onSuccess = useCallback(() => {
    showSuccessNotification(5000, translate('notifications.trackingCode.snippetFound'));

    iframeRef.current?.remove();
    iframeRef.current = null;

    updateTrackingCodeFlag(true);
    onSuccessCallback();

    setStatus(AsyncStatus.SUCCESS);
  }, [onSuccessCallback, showSuccessNotification, translate, updateTrackingCodeFlag]);

  const onMessage = useCallback(
    (event: MessageEvent<TrackingCodeValidationMessage>) => {
      if (!event || !event.data || typeof event.data !== 'object') {
        return;
      }

      const msg = event.data;

      switch (msg.type) {
        case 'TWIPLA_TRACKING_CODE_VALIDATION_RESPONSE':
          if (msg.requestId !== randomToken || msg.websiteKey !== websiteId) return;

          onSuccess();
          return;

        default:
          return;
      }
    },
    [randomToken, websiteId, onSuccess],
  );

  useListenForMessage(onMessage);

  return { verify, status };
}
